/**
 * EVENTSFLO-PDF-SUBTOTAL-BEFORE-CREDITS-V1
 *
 * Editable client-facing notes section for quotes / invoices.
 * Plain text only, 2000-char cap, manual Save (no auto-save).
 * The PDF renderer escapes the notes and preserves line breaks.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';

const MAX_LEN = 2000;

export default function EditableClientNotes({
  documentKind,   // 'quote' | 'invoice'
  documentId,
  initialNotes,
  onSaved,
}) {
  const [value, setValue] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setValue(initialNotes || '');
  }, [initialNotes]);

  const label = documentKind === 'invoice'
    ? 'Client-facing invoice notes'
    : 'Client-facing quote notes';

  const tooLong = value.length > MAX_LEN;

  const handleSave = async () => {
    if (saving || tooLong) return;
    setSaving(true);
    try {
      const url = documentKind === 'invoice'
        ? `/invoices/${encodeURIComponent(documentId)}`
        : `/quotes/${encodeURIComponent(documentId)}`;
      await api.patch(url, { notes: value });
      setSavedAt(new Date().toISOString());
      toast.success('Notes saved.');
      if (onSaved) onSaved(value);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.message || 'Save failed';
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const dirty = (value || '') !== (initialNotes || '');

  return (
    <div
      className="border border-zinc-800 rounded-md p-3 space-y-2"
      data-testid={`${documentKind}-client-notes`}
    >
      <div className="flex items-center justify-between">
        <Label className="text-xs">
          {label}{' '}
          <span className="text-muted-foreground">(plain text, appears on PDF below totals)</span>
        </Label>
        <Badge variant="outline" className="text-[10px]">
          {value.length} / {MAX_LEN}
        </Badge>
      </div>
      <Textarea
        rows={3}
        value={value}
        maxLength={MAX_LEN + 200}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Optional client-facing notes…"
        data-testid={`${documentKind}-client-notes-textarea`}
      />
      {tooLong && (
        <div
          className="text-xs text-rose-300"
          data-testid={`${documentKind}-client-notes-too-long`}
        >
          Notes must be {MAX_LEN} characters or fewer.
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        {savedAt && (
          <span
            className="text-[10px] text-emerald-300"
            data-testid={`${documentKind}-client-notes-saved-at`}
          >
            Saved.
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={!dirty || saving || tooLong}
          data-testid={`${documentKind}-client-notes-save-btn`}
        >
          {saving ? 'Saving…' : 'Save notes'}
        </Button>
      </div>
    </div>
  );
}
